import { ChangeEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import reactStringReplace from 'react-string-replace';
// import Switch from '@mui/material/Switch';

import LoginLayout from '@layouts/SignInLayout';
import {
  MDBox,
  MDInput,
  MDButton,
  MDTypography,
  If,
  FaIcon
} from '@components/index';
import {
  CLICKED_FORGOT_PASSWORD,
  CLICKED_REGISTER,
  SIGN_IN_ATTEMPT
} from '@constants/eventNames';
import { sendAmplitudeData } from '@utils/amplitude';
import bgImage from '@assets/images/login-woman.jpeg';
import useSignIn from '@hooks/useSignIn';
import UserAuth from '@services/auth';
import { Alert, IconButton, Link, Snackbar, styled } from '@mui/material';
import { useGoogleLogin } from '@react-oauth/google';
import useCustomNavigate from '@hooks/useCustomNavigate';
import { useCreateFeedBackMutation } from '@api/projects';
import FeedBackModal from '@components/FeedbackModal';
import useToast from '@hooks/useToast';
import GoogleLogo from '@assets/images/logos/google.png';
import colors from '@theme/base/colors';

const Separator = styled(MDTypography)(() => ({
  position: 'relative',
  margin: '45px auto',
  textAlign: 'center',
  '&:before, &:after': {
    content: '""',
    display: 'block',
    height: '1px',
    width: '40%',
    position: 'absolute',
    top: '50%',
    backgroundColor: 'black'
  },
  '&:before': {
    left: 0
  },
  '&:after': {
    right: 0
  }
}));

function SignIn(): JSX.Element {
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      handleSignIn(tokenResponse.code);
    },
    flow: 'auth-code'
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [errors, setErrors] = useState({ email: false, password: false });
  const [openErrorSnackBar, setOpenErrorSnackBar] = useState<boolean>();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isFeedBackModalOpen, setIsFeedBackModalOpen] = useState(false);

  const [createFeedBack, { isSuccess: isFeedbackSubmitSuccess }] =
    useCreateFeedBackMutation();

  const { pushSuccessToast } = useToast();

  const navigate = useCustomNavigate();
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get('next');

  // const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const {
    userSignIn,
    isLoginSuccess,
    isInvalidCredentials,
    isIncorrectDetails,
    errorMessage,
    setIsInvalidCredentials
  } = useSignIn();

  const onSubmitFeedBack = (payload: any) => {
    createFeedBack(payload);
  };

  const handleSignIn = async (auth_code?: string) => {
    if ((email && password) || auth_code) {
      setErrors({ email: false, password: false });
      await userSignIn(email, password, auth_code ?? '');
    } else if (!email && !password) {
      setOpenErrorSnackBar(true);
      setErrors({ email: false, password: false });
    } else {
      setErrors({ email: !email, password: !password });
    }
  };

  const handleChange = (field: 'email' | 'password', value: string) => {
    setIsInvalidCredentials(false);
    if (field === 'email') {
      setEmail(value);
      if (errors.email) setErrors({ ...errors, email: false });
    } else {
      setPassword(value);
      if (errors.password) setErrors({ ...errors, password: false });
    }
  };

  useEffect(() => {
    if (UserAuth.isAuthenticated || isLoginSuccess)
      navigate(nextUrl ?? '/project/my-projects');
  }, [UserAuth.isAuthenticated, isLoginSuccess]);

  useEffect(() => {
    if (isInvalidCredentials) setOpenErrorSnackBar(true);
  }, [isInvalidCredentials]);

  useEffect(() => {
    if (password.length === 0) setShowPassword(false);
  }, [password]);

  useEffect(() => {
    const keyDownHandler = (event: any) => {
      if (event.key === 'Enter') {
        event.target.blur();
        event.preventDefault();
        handleSignIn();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [event]);

  useEffect(() => {
    if (isFeedbackSubmitSuccess)
      pushSuccessToast(
        'Thank you! Your support request is successfully submitted. Someone from our team will get back to you within 24-48 hours.'
      );
  }, [isFeedbackSubmitSuccess]);

  return (
    <LoginLayout
      title='Hello There!'
      description='Sign in with'
      illustration={bgImage}
    >
      <MDBox component='form' role='form'>
        <MDBox mb={2}>
          <MDInput
            type='email'
            label='Email'
            fullWidth
            autoComplete='off'
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange('email', e.target.value)
            }
            error={errors.email}
            required
            helperText={
              errors.email &&
              'Please enter your e-mail ID and then click SIGN IN.'
            }
            sx={{ backgroundImage: 'none' }}
          />
        </MDBox>
        <MDBox position='relative'>
          <MDInput
            type={showPassword ? 'text' : 'password'}
            label='Password'
            fullWidth
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange('password', e.target.value)
            }
            error={errors.password}
            required
            helperText={
              errors.password &&
              'Please provide your password and then click SIGN IN.'
            }
            sx={{ backgroundImage: 'none' }}
          />
          <If condition={password.length !== 0}>
            <MDBox
              onClick={() => setShowPassword(!showPassword)}
              sx={{
                position: 'absolute',
                right: 10,
                top: 4,
                cursor: 'pointer'
              }}
            >
              <MDTypography
                variant='xs'
                color='dark'
                textTransform='uppercase'
                sx={{
                  display: { xs: 'none', sm: 'contents', fontWeight: 400 }
                }}
              >
                {showPassword ? 'Hide Password' : 'Show password'}
              </MDTypography>
              <IconButton sx={{ display: { sm: 'none' }, p: 0, mt: 0.5 }}>
                <FaIcon icon='eye' />
              </IconButton>
            </MDBox>
          </If>
        </MDBox>
        <MDBox display='flex' justifyContent='space-between' mt={1}>
          <MDTypography
            variant='sm'
            color='dark'
            underline
            sx={{ textUnderlineOffset: '2px' }}
            onClick={() => {
              sendAmplitudeData(CLICKED_FORGOT_PASSWORD);
            }}
          >
            <Link href='https://datasquirrel.ai/reset-password-request'>
              Forgot Password?
            </Link>
          </MDTypography>
          <MDTypography
            variant='sm'
            color='dark'
            underline
            sx={{ textUnderlineOffset: '2px' }}
          >
            <Link
              href='https://datasquirrel.ai/landing_page?audience=via_app_signin_page'
              onClick={() => sendAmplitudeData(CLICKED_REGISTER)}
            >
              Register
            </Link>
          </MDTypography>
        </MDBox>
        {/* <MDBox display='flex' alignItems='center' ml={-1}>
          <Switch checked={rememberMe} onChange={handleSetRememberMe} />
          <MDTypography
            variant='button'
            fontWeight='regular'
            color='text'
            onClick={handleSetRememberMe}
            sx={{ cursor: 'pointer', userSelect: 'none', ml: -1 }}
          >
            &nbsp;&nbsp;Remember me
          </MDTypography>
        </MDBox> */}

        <MDBox mt={3} mb={1}>
          <MDButton
            variant='gradient'
            color='primary'
            size='large'
            fullWidth
            onClick={() => {
              sendAmplitudeData(SIGN_IN_ATTEMPT, {
                button_clicked: 'email_password'
              });
              handleSignIn();
            }}
          >
            sign in
          </MDButton>
        </MDBox>
        <Separator variant='h5'>or</Separator>
        <MDButton
          variant='outlined'
          color='secondary'
          fullWidth
          sx={{
            borderRadius: 0,
            borderColor: colors.grey[400],
            '&.MuiButton-root': {
              '&:hover': {
                borderColor: colors.grey[500]
              }
            }
          }}
          onClick={() => {
            sendAmplitudeData(SIGN_IN_ATTEMPT, {
              button_clicked: 'Google_sign_in'
            });
            googleLogin();
          }}
        >
          <MDBox
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <MDBox
              component='img'
              src={GoogleLogo}
              alt='Google Logo'
              width='1.25rem'
              mr={1.25}
            />
            <MDTypography sx={{ fontSize: '14px', fontWeight: 500 }}>
              Login with Google
            </MDTypography>
          </MDBox>
        </MDButton>
      </MDBox>

      {isFeedBackModalOpen && (
        <FeedBackModal
          open={isFeedBackModalOpen}
          onClose={() => setIsFeedBackModalOpen(false)}
          onSubmitFeedBack={onSubmitFeedBack}
        />
      )}
      <Snackbar
        open={openErrorSnackBar}
        onClose={() => setOpenErrorSnackBar(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <Alert
          severity='error'
          sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
          onClose={() => setOpenErrorSnackBar(false)}
        >
          {isInvalidCredentials ? (
            isIncorrectDetails ? (
              <div>
                <div>
                  Invalid email or password. Please provide correct details.
                </div>
                <div>
                  If your e-mail ID is not registered,{' '}
                  <span>
                    <Link
                      target='_blank'
                      rel='noreferrer'
                      href='https://datasquirrel.ai/landing_page?audience=via_app_signin_page'
                      sx={{
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px'
                      }}
                    >
                      register
                    </Link>
                  </span>{' '}
                  here first
                </div>
              </div>
            ) : errorMessage.includes('Register here') ? (
              reactStringReplace(errorMessage, 'here', (match, index) => (
                <Link
                  key={match + index}
                  target='_blank'
                  rel='noreferrer'
                  href='https://datasquirrel.ai/landing_page?audience=via_app_signin_page'
                  sx={{
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px'
                  }}
                >
                  {match}
                </Link>
              ))
            ) : (
              errorMessage
            )
          ) : (
            'Please provide your login details.'
          )}
        </Alert>
      </Snackbar>
    </LoginLayout>
  );
}

export default SignIn;
